// `/v1/platform/*`: queries and calls for every ops page.
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { CredentialJson, PublicKeyJson } from "../auth/passkey";
import { api, buildQuery, setCsrfToken } from "./client";
import type { CostQuery, CostReport } from "./costs";
import type {
  AdminRelease,
  BugReportDetail,
  ConnectedApps,
  Items,
  LogLevel,
  OpsAuditEntry,
  OpsCompany,
  OpsDemoRequest,
  OpsDemoRequestList,
  OpsLogEvent,
  OpsMe,
  OpsPdialCarrier,
  OpsPdialCarrierPatch,
  OpsPlatformSettings,
  OpsProviderAccess,
  OpsReport,
  OpsStaffMember,
  Page,
  PatchRelease,
  PushResult,
  ReleaseList,
  ResolveReports,
  ResolveResult,
  ServerSetting,
  WhatsNewList,
} from "./types";

const P = "/v1/platform";
const enc = encodeURIComponent;

export const qk = {
  me: ["ops", "me"] as const,
  companies: ["ops", "companies"] as const,
  providerAccess: (company: string) => ["ops", "companies", company, "provider-access"] as const,
  reports: (company: string | undefined) => ["ops", "reports", company ?? "all"] as const,
  allReports: ["ops", "reports"] as const,
  report: (company: string, id: string) => ["ops", "report", company, id] as const,
  releases: ["ops", "releases"] as const,
  connected: ["ops", "releases", "connected"] as const,
  whatsNew: ["ops", "whats-new"] as const,
  logs: (company: string | undefined, level: string | undefined) =>
    ["ops", "logs", company ?? "all", level ?? "all"] as const,
  server: ["ops", "server"] as const,
  staff: ["ops", "staff"] as const,
  demoRequests: ["ops", "demo-requests"] as const,
  audit: ["ops", "audit"] as const,
  settings: ["ops", "settings"] as const,
  costs: (company: string, q: CostQuery) => ["ops", "companies", company, "costs", q] as const,
  pdialCarrier: (company: string) => ["ops", "companies", company, "pdial-carrier"] as const,
};

/** The outside-calling gate, its readiness checklist and the emergency stop (ADR 0054). */
export const settingsQuery = queryOptions({
  queryKey: qk.settings,
  queryFn: () => api<OpsPlatformSettings>(`${P}/settings`),
});

export const platform = {
  setSetting: (
    key: "outside_telephony" | "outbound_stop" | "customer_storage" | "require_staff_passkey",
    value: string,
    note?: string,
  ) =>
    api<OpsPlatformSettings>(`${P}/settings/${enc(key)}`, {
      method: "PUT",
      body: { value, ...(note ? { note } : {}) },
    }),
  record: (item: string, reference: string | null, note?: string) =>
    api<OpsPlatformSettings>(`${P}/readiness/${enc(item)}`, {
      method: "PUT",
      body: { reference, ...(note ? { note } : {}) },
    }),
};

/** The signed-in staff member. Every answer carries the CSRF token writes send. */
export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: async () => {
    const me = await api<OpsMe>(`${P}/me`);
    setCsrfToken(me.csrf_token);
    return me;
  },
  staleTime: 60_000,
  retry: false,
});

export const auth = {
  passkeyOptions: () => api<{ public_key: PublicKeyJson }>(`${P}/auth/passkey/options`, { method: "POST" }),
  passkey: (credential: CredentialJson) =>
    api<OpsMe>(`${P}/auth/passkey`, { method: "POST", body: { credential } }),
  dev: (email: string) => api<OpsMe>(`${P}/auth/dev`, { method: "POST", body: { email } }),
  /** Signed out: the customer app's origin, where a handoff is asked for. */
  handoffOrigin: () => api<{ app_url: string }>(`${P}/auth/handoff`),
  redeemHandoff: (token: string, verifier: string) =>
    api<OpsMe>(`${P}/auth/handoff`, { method: "POST", body: { token, verifier } }),
  enrollOptions: () =>
    api<{ public_key: PublicKeyJson }>(`${P}/auth/passkey/enroll/options`, { method: "POST" }),
  enroll: (credential: CredentialJson) =>
    api<void>(`${P}/auth/passkey/enroll`, { method: "POST", body: { credential } }),
  logout: () => api<void>(`${P}/auth/logout`, { method: "POST" }),
};

export const companiesQuery = queryOptions({
  queryKey: qk.companies,
  queryFn: () => api<Items<OpsCompany>>(`${P}/companies`),
});

export const providerAccessQuery = (company: string) =>
  queryOptions({
    queryKey: qk.providerAccess(company),
    queryFn: () => api<OpsProviderAccess>(`${P}/companies/${enc(company)}/provider-access`),
  });

export const reportsQuery = (company: string | undefined) =>
  queryOptions({
    queryKey: qk.reports(company),
    queryFn: () => api<Items<OpsReport>>(`${P}/reports`, { query: { company, limit: 100 } }),
  });

export const reportQuery = (company: string, id: string) =>
  queryOptions({
    queryKey: qk.report(company, id),
    queryFn: () => api<BugReportDetail>(`${P}/companies/${enc(company)}/reports/${enc(id)}`),
  });

export const releasesQuery = queryOptions({
  queryKey: qk.releases,
  queryFn: () => api<ReleaseList>(`${P}/releases`),
});

export const connectedQuery = queryOptions({
  queryKey: qk.connected,
  queryFn: () => api<ConnectedApps>(`${P}/releases/connected`),
});

export const whatsNewQuery = queryOptions({
  queryKey: qk.whatsNew,
  queryFn: () => api<WhatsNewList>(`${P}/whats-new`),
});

export const LOG_PAGE = 100;

export const logsInfinite = (company: string | undefined, level: LogLevel | undefined) =>
  infiniteQueryOptions({
    queryKey: qk.logs(company, level),
    queryFn: ({ pageParam }) =>
      api<Page<OpsLogEvent>>(`${P}/logs`, {
        query: { company, level, limit: LOG_PAGE, cursor: pageParam },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const serverQuery = queryOptions({
  queryKey: qk.server,
  queryFn: () => api<Items<ServerSetting>>(`${P}/server`),
});

export const staffQuery = queryOptions({
  queryKey: qk.staff,
  queryFn: () => api<Items<OpsStaffMember>>(`${P}/staff`),
});

export const demoRequestsQuery = queryOptions({
  queryKey: qk.demoRequests,
  queryFn: () => api<OpsDemoRequestList>(`${P}/demo-requests`),
});

export const auditInfinite = infiniteQueryOptions({
  queryKey: qk.audit,
  queryFn: ({ pageParam }) =>
    api<Page<OpsAuditEntry>>(`${P}/audit`, { query: { limit: 50, cursor: pageParam } }),
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last) => last.next_cursor ?? undefined,
});

/** Kite's costs for one company: the customer console's report, read only (ADR 0005). */
export const costsQuery = (company: string, q: CostQuery) =>
  queryOptions({
    queryKey: qk.costs(company, q),
    queryFn: () => api<CostReport>(`${P}/companies/${enc(company)}/costs`, { query: { ...q } }),
    placeholderData: (p) => p,
  });

/** The CSV of the same report, narrowed like the page. Same origin on the ops host. */
export const costsExportPath = (company: string, q: CostQuery) =>
  `${P}/companies/${enc(company)}/costs/export.csv${buildQuery({ ...q })}`;

/** Parallel dialing's carrier tuning for one company (ADR 0050). */
export const pdialCarrierQuery = (company: string) =>
  queryOptions({
    queryKey: qk.pdialCarrier(company),
    queryFn: () => api<OpsPdialCarrier>(`${P}/companies/${enc(company)}/pdial-carrier`),
  });

export const ops = {
  setProviderAccess: (company: string, body: OpsProviderAccess) =>
    api<OpsProviderAccess>(`${P}/companies/${enc(company)}/provider-access`, { method: "PUT", body }),
  resolveReports: (company: string, body: ResolveReports) =>
    api<ResolveResult>(`${P}/companies/${enc(company)}/reports/resolve`, { method: "POST", body }),
  reopenReport: (company: string, id: string) =>
    api<void>(`${P}/companies/${enc(company)}/reports/${enc(id)}/reopen`, { method: "POST" }),
  patchRelease: (id: string, body: PatchRelease) =>
    api<AdminRelease>(`${P}/releases/${enc(id)}`, { method: "PATCH", body }),
  pushRelease: (id: string) => api<PushResult>(`${P}/releases/${enc(id)}/push`, { method: "POST" }),
  yankRelease: (id: string, reason: string | null) =>
    api<AdminRelease>(`${P}/releases/${enc(id)}/yank`, { method: "POST", body: { reason } }),
  unyankRelease: (id: string) => api<AdminRelease>(`${P}/releases/${enc(id)}/unyank`, { method: "POST" }),
  setDemoRequest: (id: string, status: OpsDemoRequest["status"]) =>
    api<OpsDemoRequest>(`${P}/demo-requests/${enc(id)}`, { method: "PATCH", body: { status } }),
  addStaff: (email: string) => api<Items<OpsStaffMember>>(`${P}/staff`, { method: "POST", body: { email } }),
  removeStaff: (email: string) =>
    api<Items<OpsStaffMember>>(`${P}/staff/${enc(email)}`, { method: "DELETE" }),
  putPdialCarrier: (company: string, body: OpsPdialCarrierPatch) =>
    api<OpsPdialCarrier>(`${P}/companies/${enc(company)}/pdial-carrier`, { method: "PUT", body }),
  checkPdialCarrier: (company: string) =>
    api<OpsPdialCarrier>(`${P}/companies/${enc(company)}/pdial-carrier/check`, { method: "POST" }),
};
